import { useCallback, useEffect, useRef, useState } from 'react'

interface Point {
  x: number
  y: number
}

interface UseHoverIntentReturn {
  isHovered: boolean
  mousePoint: Point | null
  onCardMouseEnter: (e: React.MouseEvent) => void
  onCardMouseLeave: () => void
  onPanelMouseEnter: () => void
  onPanelMouseLeave: () => void
}

const OPEN_DELAY = 300
const CLOSE_GRACE_PERIOD = 150

export function useHoverIntent(): UseHoverIntentReturn {
  const [isHovered, setIsHovered] = useState(false)
  const [mousePoint, setMousePoint] = useState<Point | null>(null)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastMouse = useRef<Point>({ x: 0, y: 0 })

  const clearTimers = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const close = useCallback(() => {
    clearTimers()
    setIsHovered(false)
    setMousePoint(null)
  }, [clearTimers])

  const onCardMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      lastMouse.current = { x: e.clientX, y: e.clientY }
      if (closeTimer.current) {
        clearTimeout(closeTimer.current)
        closeTimer.current = null
      }
      if (!isHovered) {
        openTimer.current = setTimeout(() => {
          setMousePoint({ ...lastMouse.current })
          setIsHovered(true)
        }, OPEN_DELAY)
      }
    },
    [isHovered]
  )

  const onCardMouseLeave = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
    closeTimer.current = setTimeout(() => {
      setIsHovered(false)
      setMousePoint(null)
    }, CLOSE_GRACE_PERIOD)
  }, [])

  const onPanelMouseEnter = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const onPanelMouseLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setIsHovered(false)
      setMousePoint(null)
    }, CLOSE_GRACE_PERIOD)
  }, [])

  useEffect(() => {
    if (!isHovered) return

    const handleScroll = () => {
      close()
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isHovered, close])

  useEffect(() => {
    return clearTimers
  }, [clearTimers])

  return {
    isHovered,
    mousePoint,
    onCardMouseEnter,
    onCardMouseLeave,
    onPanelMouseEnter,
    onPanelMouseLeave
  }
}
